import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CAVisibilityRankings from "../CategoryAnalysis/CAVisibilityRankings";
import BaseResponsesModal from "../../Component/BaseResponseComponent/BaseResponsesModal";
import useBaseResponsesModal from "../../Component/BaseResponseComponent/useBaseResponsesModal";
import CMConsensusCard from "./CMConsensusCard";
import CMPodiumConsensus from "./CMPodiumConsensus";
import MUISelect from "../../Component/UI/MUISelect/MUISelect";
import classes from "./CategoryMonitoring.module.css";

const OVERALL_CONSENSUS_VIEW = "podium"; // "podium" | "chart"

export default function CMVisibilitySections({
  llmMode,
  consensusData,
  consensusLoading,
  rawData,
  rawLoading,
  llmOptions = [],
  rawDatasetLlm,
  onRawDatasetLlmChange,
}) {
  const {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    closeInfoModal,
    handleInfoClick,
  } = useBaseResponsesModal(rawData?.extras, "visibility");

  const isAllMode = llmMode === "all";

  const summarySource = isAllMode ? consensusData : rawData;
  const { attribute_consensus, final_avg } = summarySource || {};
  const attributeKeys = Object.keys(attribute_consensus || {});

  const extras = rawData?.extras;
  const productRankingsArray = extras?.productRankingsArray ?? [];
  const productRankingsData = extras?.productRankingsData ?? [];
  const productMappingAttribute = extras?.productMappingAttribute ?? {};
  const rawAttributeKeys = Object.keys(productMappingAttribute);

  const overallRankLabels = isAllMode ? undefined : attributeKeys;
  const attributeSetLabels = (keyword) =>
    isAllMode ? undefined : (productMappingAttribute[keyword] ?? []).map((_, i) => `Set ${i + 1}`);

  const summaryLoading = isAllMode ? consensusLoading : rawLoading;
  const SectionLoadingOverlay = ({ show }) =>
    show ? (
      <div className={classes.sectionLoadingOverlay}>
        <CircularProgress size={28} thickness={4} />
      </div>
    ) : null;

  const normalizeBrandKey = (name) => (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const allDomainEntries = productRankingsData.flat().filter((item) => item?.name && item?.domain);

  const resolveDomain = (brandName) => {
    const target = normalizeBrandKey(brandName);
    if (!target) return null;

    const exact = allDomainEntries.find((item) => normalizeBrandKey(item.name) === target);
    if (exact) return exact.domain;

    const partial = allDomainEntries.find((item) => {
      const key = normalizeBrandKey(item.name);
      return key.includes(target) || target.includes(key);
    });
    return partial?.domain || null;
  };

  const consensusNames = new Set([
    ...(final_avg || []).map((entry) => entry.name),
    ...attributeKeys.flatMap((keyword) => (attribute_consensus?.[keyword] || []).map((entry) => entry.name)),
  ]);
  const domainMap = {};
  consensusNames.forEach((name) => {
    const domain = resolveDomain(name);
    if (domain) domainMap[name] = domain;
  });

  // Same fuzzy fallback applied to the raw per-run datasets in Section 3.
  const enrichedProductRankingsData = productRankingsData.map((set) =>
    (set || []).map((item) => ({
      ...item,
      domain: item?.domain || resolveDomain(item?.name),
    }))
  );

  return (
    <div>
      {/* Section 1: Overall Consensus Ranking */}
      <div className={classes.section}>
        <SectionLoadingOverlay show={summaryLoading} />
        <div className={classes.sectionHeader}>
          <span className={classes.sectionTitle}>Overall Consensus Ranking</span>
          <span className={classes.sectionSubtitle}>
            {isAllMode ? "Combined ranking across all LLMs" : "Combined ranking across all rank attributes"}
          </span>
        </div>
        {OVERALL_CONSENSUS_VIEW === "podium" ? (
          <CMPodiumConsensus title="Overall Consensus" entries={final_avg} rankLabels={overallRankLabels} domainMap={domainMap} />
        ) : (
          <CMConsensusCard title="Overall Consensus" entries={final_avg} rankLabels={overallRankLabels} domainMap={domainMap} wide />
        )}
      </div>

      {/* Section 2: Attribute Averages */}
      <div className={classes.section}>
        <SectionLoadingOverlay show={summaryLoading} />
        <div className={classes.sectionHeader}>
          <span className={classes.sectionTitle}>Attribute Averages</span>
          <span className={classes.sectionSubtitle}>
            {isAllMode ? "Consensus ranking per rank attribute, across all LLMs" : "Consensus ranking per rank attribute"}
          </span>
        </div>
        <div className={classes.consensusGrid}>
          {attributeKeys.map((keyword) => (
            <CMConsensusCard
              key={keyword}
              title={keyword}
              entries={attribute_consensus?.[keyword]}
              rankLabels={attributeSetLabels(keyword)}
              domainMap={domainMap}
            />
          ))}
        </div>
      </div>

      {/* Section 3: Raw per-run datasets for a single selected LLM */}
      <div className={classes.section}>
        <SectionLoadingOverlay show={rawLoading} />
        <div
          className={classes.sectionHeader}
          style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 12 }}
        >
          <div>
            <span className={classes.sectionTitle}>Raw Datasets</span>
            <span className={classes.sectionSubtitle}>Every individual run, before consensus</span>
          </div>
          {llmOptions.length > 1 && (
            <div style={{ minWidth: 180 }}>
              <MUISelect
                label="LLM"
                options={llmOptions}
                optionValue={rawDatasetLlm}
                onChange={(e) => onRawDatasetLlmChange(e.target.value)}
              />
            </div>
          )}
        </div>
        {rawAttributeKeys.map((keyword) => {
          const indices = productMappingAttribute[keyword] ?? [];
          if (!indices.length) return null;
          return (
            <div key={keyword} className={classes.keywordGroup}>
              <div className={classes.keywordGroupLabel} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {keyword}
                <Tooltip title="View Base Prompt & Responses" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleInfoClick(keyword, indices)}
                    sx={{ padding: "2px", "&:hover": { backgroundColor: "primary.light", color: "primary.main" } }}
                  >
                    <InfoOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </div>
              <CAVisibilityRankings
                rankings={indices.map((i) => productRankingsArray[i])}
                productRankingDataSets={indices.map((i) => enrichedProductRankingsData[i] ?? null)}
                pIndices={indices}
                sectionIndices={indices.map((_, i) => i)}
              />
            </div>
          );
        })}
      </div>

      <BaseResponsesModal
        open={infoModalOpen}
        title={infoModalTitle}
        content={infoModalContent}
        onClose={closeInfoModal}
      />
    </div>
  );
}
